import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextField } from "@/components/base/FormControls";
import { transferStock } from "@/lib/inventoryApi";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Branch } from "@/types";
import type { ProductOption } from "@/hooks/useInventory";

interface TransferModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  products: ProductOption[];
  branches: Branch[];
  defaultBranchId: string | null;
}

export default function TransferModal({
  open,
  onClose,
  onSaved,
  products,
  branches,
  defaultBranchId,
}: TransferModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [productId, setProductId] = useState("");
  const [fromBranchId, setFromBranchId] = useState("");
  const [toBranchId, setToBranchId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const activeBranches = useMemo(() => branches.filter((branch) => branch.active), [branches]);

  useEffect(() => {
    if (!open) return;
    setProductId("");
    setFromBranchId(defaultBranchId ?? activeBranches[0]?.id ?? "");
    setToBranchId(activeBranches.find((branch) => branch.id !== (defaultBranchId ?? ""))?.id ?? "");
    setQuantity("");
    setReason("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultBranchId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!productId) {
      setError("Selecciona un producto.");
      return;
    }
    if (!fromBranchId || !toBranchId) {
      setError("Selecciona la sucursal de origen y destino.");
      return;
    }
    const qty = Number.parseFloat(quantity.replace(",", "."));
    if (!Number.isFinite(qty) || qty <= 0) {
      setError("Ingresa una cantidad mayor que cero.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await transferStock({
        productId,
        fromBranchId,
        toBranchId,
        quantity: qty,
        unit: products.find((product) => product.id === productId)?.unit ?? null,
        reason: reason.trim() || null,
        userId: profile?.id ?? null,
      });

      if (!result.ok) {
        setError(result.error ?? "No se pudo realizar la transferencia.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "inventario",
        entity: "inventory",
        entityId: productId,
        branchId: fromBranchId,
        newValue: { transferencia: { from: fromBranchId, to: toBranchId, quantity: qty } },
      });

      toast.success("Transferencia realizada correctamente.");
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title="Transferir entre sucursales"
      subtitle="Registra una salida en la sucursal de origen y una entrada en la de destino."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="transfer-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            Transferir
          </button>
        </>
      }
    >
      <form id="transfer-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <SelectField
          label="Producto"
          name="product_id"
          value={productId}
          onChange={setProductId}
          placeholder="Selecciona un producto"
          required
          options={products.map((product) => ({
            value: product.id,
            label: product.sku ? `${product.name} (${product.sku})` : product.name,
          }))}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Sucursal de origen"
            name="from_branch_id"
            value={fromBranchId}
            onChange={setFromBranchId}
            placeholder="Origen"
            required
            options={activeBranches.map((branch) => ({ value: branch.id, label: branch.name }))}
          />
          <SelectField
            label="Sucursal de destino"
            name="to_branch_id"
            value={toBranchId}
            onChange={setToBranchId}
            placeholder="Destino"
            required
            options={activeBranches
              .filter((branch) => branch.id !== fromBranchId)
              .map((branch) => ({ value: branch.id, label: branch.name }))}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Cantidad"
            name="quantity"
            value={quantity}
            onChange={setQuantity}
            inputMode="numeric"
            placeholder="0"
            required
          />
          <TextField
            label="Motivo"
            name="reason"
            value={reason}
            onChange={setReason}
            placeholder="Abastecimiento, traslado..."
          />
        </div>

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}